// src/components/chat/feedbackStyles.ts

import * as React from "react";

/**
 * Shared styles for feedback components
 */

// Common styles used across feedback components
const baseStyles = {
  fontFamily: "'Segoe UI', sans-serif",
  transition: "all 0.2s ease",
} as const;

// FeedbackButtons component styles
export const feedbackButtonsStyles = {
  container: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  } as React.CSSProperties,

  button: (hasSubmitted: boolean, isHovered: boolean): React.CSSProperties => ({
    ...baseStyles,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    padding: "6px",
    cursor: hasSubmitted ? "default" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: hasSubmitted ? "none" : "auto",
    transform: !hasSubmitted && isHovered ? "scale(1.1)" : "scale(1)",
  }),
};

// FeedbackModal component styles
export const feedbackModalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "fadeIn 0.2s ease",
  } as React.CSSProperties,

  modal: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    width: "90%",
    maxWidth: "480px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    animation: "slideUp 0.3s ease",
    position: "relative",
  } as React.CSSProperties,

  header: {
    ...baseStyles,
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontWeight: "600",
    color: "#333333",
  } as React.CSSProperties,

  closeButton: (isSubmitting: boolean): React.CSSProperties => ({
    position: "absolute",
    top: "16px",
    right: "16px",
    backgroundColor: "transparent",
    border: "none",
    cursor: isSubmitting ? "not-allowed" : "pointer",
    padding: "4px",
    opacity: isSubmitting ? 0.5 : 1,
  }),

  textarea: (isSubmitting: boolean, isFocused: boolean): React.CSSProperties => ({
    ...baseStyles,
    width: "100%",
    minHeight: "120px",
    maxHeight: "300px",
    resize: "vertical",
    padding: "12px",
    border: `1px solid ${isFocused ? "#0078D4" : "#E0E0E0"}`,
    borderRadius: "8px",
    fontSize: "14px",
    color: "#333333",
    backgroundColor: isSubmitting ? "#F5F5F5" : "white",
    outline: "none",
    boxSizing: "border-box", // Fix alignment by including padding/border in width
  }),

  errorMessage: {
    ...baseStyles,
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "#FEE",
    border: "1px solid #FCC",
    borderRadius: "4px",
    color: "#C00",
    fontSize: "13px",
  } as React.CSSProperties,

  submitButton: (isSubmitting: boolean, isHovered: boolean): React.CSSProperties => ({
    ...baseStyles,
    width: "100%",
    marginTop: "16px",
    padding: "12px 24px",
    backgroundColor: isSubmitting ? "#999999" : isHovered ? "#0A5A7A" : "#0F7599",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: isSubmitting ? "not-allowed" : "pointer",
    opacity: isSubmitting ? 0.7 : 1,
  }),

  thankYouContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    textAlign: "center",
  } as React.CSSProperties,

  thankYouIcon: {
    width: "64px",
    height: "64px",
    marginBottom: "24px",
  } as React.CSSProperties,

  thankYouTitle: {
    ...baseStyles,
    margin: "0 0 16px 0",
    fontSize: "24px",
    fontWeight: "600",
    color: "#333333",
  } as React.CSSProperties,

  thankYouText: {
    ...baseStyles,
    margin: 0,
    fontSize: "16px",
    color: "#666666",
    lineHeight: "1.5",
  } as React.CSSProperties,
};

// Icon colors for different states
export const getIconColor = (
  rating: "thumbs_up" | "thumbs_down",
  selectedRating: "thumbs_up" | "thumbs_down" | null,
  hoveredButton: "thumbs_up" | "thumbs_down" | null,
  hasSubmitted: boolean
): string => {
  const isSelected = selectedRating === rating;
  const isHovered = hoveredButton === rating;

  if (hasSubmitted) {
    return isSelected ? "#000000" : "#E5E5E5";
  }

  if (isHovered) {
    return "#333333";
  }

  return "#707070";
};
